import { TextAlignLeft } from "@phosphor-icons/react";
import { useMemo } from "react";

import { PaginatedDataTable } from "common/components/DataTable";
import usePaginatedDataTable from "common/components/DataTable/usePaginatedDataTable";
import ProfileImage from "common/components/ui/ProfileImage";
import Skeleton from "common/components/ui/Skeleton";

import BlacklistRowMenu from "./BlacklistRowMenu";
import DesktopManageBlacklist from "./DesktopManageBlacklist";

import type { ColumnDef } from "@tanstack/react-table";
import type { Company } from "../../../types";

export type BlacklistCompanyRow = Company & { id: string };

interface BlacklistCompaniesTableProps {
  companies: BlacklistCompanyRow[];
  selectedRows: BlacklistCompanyRow[];
  setSelectedRows: (rows: BlacklistCompanyRow[]) => void;
  isLoading: boolean;
}

export default function BlacklistCompaniesTable({
  companies,
  selectedRows,
  setSelectedRows,
  isLoading,
}: BlacklistCompaniesTableProps) {
  const columns = useMemo(() => {
    const cols: ColumnDef<BlacklistCompanyRow>[] = [
      {
        id: "company",
        cell: ({
          row: {
            original: { logo_url, name },
          },
        }) => {
          return isLoading ? (
            <>
              <Skeleton className="rounded-full size-8" />
              <Skeleton className="w-28 h-4 ml-1.5" />
            </>
          ) : (
            <>
              <ProfileImage src={logo_url} size="sm" />
              <span className="text-xs-bold truncate ml-1.5">{name}</span>
            </>
          );
        },
        enableHiding: false,
      },
      {
        id: "description",
        header: "Description",
        cell: ({
          row: {
            original: { description },
          },
        }) =>
          isLoading ? (
            <Skeleton className="h-4 w-40" />
          ) : (
            <span className="line-clamp-2 break-all">{description || "—"}</span>
          ),
        meta: { icon: TextAlignLeft, className: "min-w-40" },
        enableHiding: false,
      },
      {
        id: "action",
        cell: ({ row: { original } }) =>
          isLoading ? (
            <Skeleton className="rounded-full size-8 ml-auto" />
          ) : (
            <BlacklistRowMenu item={original} />
          ),
        enableHiding: false,
        meta: { className: "w-0", isSticky: true },
      },
    ];
    return cols;
  }, [isLoading]);

  const { table } = usePaginatedDataTable({
    dataTableOptions: {
      localStorageId: "blacklist-companies",
      onRowSelectionChange: setSelectedRows,
      selectedRows,
    },
    reactTableOptions: {
      data: companies,
      columns,
    },
    isLoading,
  });

  return (
    <PaginatedDataTable
      title="Companies"
      table={table}
      isLoading={isLoading}
      selectionActionsComponent={
        <DesktopManageBlacklist
          selectedItems={selectedRows}
          resetSelection={() => setSelectedRows([])}
          blacklistType="companies"
        />
      }
    />
  );
}
